import { useContext, useMemo, useState } from 'react';
import { ApiContext, errorText } from '../../lib/api';
import {
  DRY_RUN_ARG_HINT,
  DRY_RUN_EVENTS,
  dryRun,
  suggestScript,
  type DryRunEvent,
  type DryRunResult,
} from '../../lib/scenarioDryRun';
import { STORY_ON_FAIL } from '../../lib/s4/types';
import { newEffect } from '../../lib/story/effects';
import type { CondDef, EffectDef } from '../../lib/story/types';
import { previewSetPiece, type SetPiecePreviewResult } from '../../lib/storyApi';
import type { FieldErrors, Issue } from '../../lib/validate';
import {
  ACTOR_ROLES,
  ACTOR_STATES,
  ANCHOR_KINDS,
  DOOR_OPS,
  HUD_CONTROLS,
  LOSE_TYPES,
  MAP_BADGE_KINDS,
  OBJECTIVE_TYPES,
  SCENARIO_ACTION_TYPES,
  SCENARIO_EVENT_TYPES,
  SERVER_ACTIONS,
  SET_PIECE_PLACEMENTS,
  DOOR_POLICIES,
  SET_PIECE_ROTATIONS,
  SET_PIECE_SIDE,
  TICKET_KINDS,
  WORLD_FLAG_SCOPES,
  WORLD_FLAG_TYPES,
  condFacts,
  findFlag,
  type ObjectiveDef,
  type ScenarioActionDef,
  type ScenarioDef,
  type ScenarioEventDef,
  type ScenarioTriggerDef,
  type SetPieceAnchorDef,
  type SetPieceDef,
  type WorldFlagDef,
} from '../../lib/validate/scenarios';
import { ConditionPicker } from '../story/ConditionPicker';
import { SCENARIO_NAMESPACES } from '../../lib/story/facts';
import { EffectListEditor } from '../story/EffectEditor';
import {
  CheckboxField,
  Field,
  Fieldset,
  IssueList,
  ListEditor,
  NumberField,
  NumberInput,
  SelectField,
  SelectInput,
  StringListEditor,
  TextField,
  inputCls,
  smallBtn,
} from './fields';
import { useContentBundle, type Option } from './refs';
import type { FormProps } from './types';

const opts = (list: readonly string[]): Option[] => list.map((v) => ({ value: v, label: v }));

/** Issues at `base` and under it, re-keyed relative to `base` (for pickers that take relative paths). */
const relative = (errors: FieldErrors, base: string): Record<string, Issue[]> => {
  const out: Record<string, Issue[]> = {};
  for (const [k, v] of Object.entries(errors))
    if (k === base) out[''] = v;
    else if (k.startsWith(`${base}.`)) out[k.slice(base.length + 1)] = v;
  return out;
};

/** Toggle chips over a list of strings (categories, an objective's `after`). Values not among the options stay shown. */
function Chips({
  label,
  options,
  value,
  onChange,
  issues,
  hint,
}: {
  label: string;
  options: Option[];
  value: string[];
  onChange: (next: string[]) => void;
  issues?: Issue[];
  hint?: string;
}) {
  const unknown = value.filter((v) => !options.some((o) => o.value === v));
  return (
    <div className="sm:col-span-2">
      <span className="block text-xs font-semibold uppercase tracking-wide">{label}</span>
      <div role="group" aria-label={label} className="mt-0.5 flex flex-wrap gap-1">
        {options.length === 0 && unknown.length === 0 && (
          <span className="text-xs text-bruise">None to pick.</span>
        )}
        {options.map((o) => {
          const on = value.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={on}
              className={`${smallBtn} ${on ? 'bg-nonphoto' : ''}`}
              onClick={() =>
                onChange(on ? value.filter((v) => v !== o.value) : [...value, o.value])
              }
            >
              {o.label}
            </button>
          );
        })}
        {unknown.map((v) => (
          <button
            key={v}
            type="button"
            aria-pressed
            className={`${smallBtn} bg-blam text-white`}
            title="Not in the draft: click to remove"
            onClick={() => onChange(value.filter((x) => x !== v))}
          >
            {v} ✕
          </button>
        ))}
      </div>
      {hint && <p className="mt-0.5 text-xs text-bruise">{hint}</p>}
      <IssueList issues={issues} />
    </div>
  );
}

/** What the scenario form's pickers offer: draft lists plus this scenario's own ids. */
type Ctx = {
  objectives: string[];
  actors: string[];
  timers: string[];
  anchors: Option[];
  pieces: string[];
  beats: Option[];
  zombies: Option[];
  npcs: Option[];
  flags: WorldFlagDef[];
};

function useScenarioCtx(s: ScenarioDef): Ctx {
  const b = useContentBundle();
  const setPieces = (b.setPieces ?? []).filter((x): x is SetPieceDef => x != null);
  const pieces = (s.setPieces ?? []).map((r) => r?.id ?? '').filter(Boolean);
  return {
    objectives: (s.objectives ?? []).map((o) => o?.id ?? '').filter(Boolean),
    actors: (s.actors ?? []).map((a) => a?.id ?? '').filter(Boolean),
    timers: [
      ...new Set(
        (s.triggers ?? []).flatMap((t) =>
          (t?.do ?? []).filter((a) => a?.type === 'timer' && a.id).map((a) => a!.id!),
        ),
      ),
    ],
    pieces,
    anchors: pieces.flatMap((id) =>
      (setPieces.find((sp) => sp.id === id)?.anchors ?? [])
        .filter((a): a is SetPieceAnchorDef => a != null && !!a.id)
        .map((a) => ({ value: `${id}.${a.id}`, label: `${id}.${a.id} (${a.kind ?? ''})` })),
    ),
    beats: (b.beatChains ?? [])
      .filter((c) => c != null && c.id)
      .map((c) => ({ value: c!.id!, label: `${c!.title || c!.id} (${c!.id})` })),
    zombies: (b.zombies ?? []).map((z) => ({ value: z.id ?? '', label: z.name || (z.id ?? '') })),
    npcs: (b.npcs ?? [])
      .filter((n) => n != null && n.id)
      .map((n) => ({ value: n!.id!, label: n!.id! })),
    flags: (b.worldFlags ?? []).filter((f): f is WorldFlagDef => f != null),
  };
}

// ----- scenario --------------------------------------------------------------------------------------------------------------

/**
 * A scenario (scenario.html §3 and §5): basics, the building rule, set pieces, actors, ordered objectives with `after` chips and
 * per-type params, the scenario-trigger table (on / if / do), lose rules, onWin/onLose, and a dry run of `ScenarioRules`.
 */
export function ScenarioForm({ value, onChange, errors }: FormProps<ScenarioDef>) {
  const s = value;
  const set = (patch: Partial<ScenarioDef>) => onChange({ ...s, ...patch });
  const ctx = useScenarioCtx(s);
  const b = useContentBundle();
  const setPieceOptions = (b.setPieces ?? [])
    .filter((x) => x != null && x.id)
    .map((x) => ({ value: x!.id!, label: `${x!.name || x!.id} (${x!.id})` }));
  const building = s.building ?? {};
  const setBuilding = (patch: Partial<NonNullable<ScenarioDef['building']>>) =>
    set({ building: { ...building, ...patch } });
  const markOptions = [{ value: 'exit', label: 'exit' }, ...ctx.anchors];
  const zoneOptions = [
    ...ctx.pieces.map((p) => ({ value: p, label: `${p} (whole room)` })),
    ...ctx.anchors,
  ];

  return (
    <>
      <Fieldset legend="Scenario">
        <TextField
          label="Id"
          mono
          value={s.id}
          issues={errors.id}
          onChange={(v) => set({ id: v })}
        />
        <TextField
          label="Title"
          value={s.title}
          issues={errors.title}
          onChange={(v) => set({ title: v })}
        />
        <SelectField
          label="Kind"
          value={s.kind || 'interior'}
          options={opts(TICKET_KINDS)}
          hint="interior and comic play in Step 1; arena and overwatch come in Step 3; multi_floor is reserved."
          issues={errors.kind}
          onChange={(v) => set({ kind: v ?? 'interior' })}
        />
      </Fieldset>

      <Fieldset legend="Building rule">
        <Chips
          label="Categories"
          options={(b.categories ?? []).map((c) => ({ value: c.id ?? '', label: c.id ?? '' }))}
          value={building.categories ?? []}
          hint="None = any category."
          issues={Object.values(relative(errors, 'building.categories')).flat()}
          onChange={(v) => setBuilding({ categories: v })}
        />
        <StringListEditor
          label="Building kinds"
          value={building.kinds}
          errors={errors}
          base="building.kinds"
          placeholder="hospital"
          hint="buildings.kind values (lower case and _); none = any."
          onChange={(v) => setBuilding({ kinds: v })}
        />
        <NumberField
          label="Min rooms"
          integer
          min={0}
          max={16}
          value={building.minRooms ?? 0}
          hint="0 = the building's own count. A big set piece needs room to fit."
          issues={errors['building.minRooms']}
          onChange={(v) => setBuilding({ minRooms: v ?? 0 })}
        />
      </Fieldset>

      <Fieldset legend="Set pieces">
        <ListEditor
          label="Set pieces (at most 2)"
          what="set piece"
          items={s.setPieces ?? []}
          issues={errors.setPieces}
          make={() => ({ id: '', placement: 'deepest', doorPolicy: '', doorTag: '' })}
          onChange={(v) => set({ setPieces: v })}
          row={(r, i, setRow) => (
            <div className="grid gap-2 sm:grid-cols-4">
              <SelectField
                label={`Set piece ${i + 1}`}
                value={r.id}
                options={setPieceOptions}
                issues={errors[`setPieces.${i}.id`]}
                onChange={(v) => setRow({ ...r, id: v ?? '' })}
              />
              <SelectField
                label="Placement"
                value={r.placement || 'any'}
                options={opts(SET_PIECE_PLACEMENTS)}
                issues={errors[`setPieces.${i}.placement`]}
                onChange={(v) => setRow({ ...r, placement: v ?? 'any' })}
              />
              <SelectField
                label="Door policy"
                value={r.doorPolicy ?? ''}
                none="keep the doors"
                options={[
                  { value: 'one', label: 'one (seal the others)' },
                  { value: 'any', label: 'any' },
                ]}
                issues={errors[`setPieces.${i}.doorPolicy`]}
                onChange={(v) => setRow({ ...r, doorPolicy: v ?? '' })}
              />
              <TextField
                label="Door tag"
                mono
                value={r.doorTag}
                hint="e.g. chained"
                onChange={(v) => setRow({ ...r, doorTag: v })}
              />
            </div>
          )}
        />
      </Fieldset>

      <Fieldset legend="Actors">
        <ListEditor
          label="Actors (NPCs, at most 4)"
          what="actor"
          items={s.actors ?? []}
          issues={errors.actors}
          make={() => ({
            id: '',
            role: 'rescuee',
            npcId: '',
            anchor: '',
            health: 100,
            protected: false,
          })}
          onChange={(v) => set({ actors: v })}
          row={(a, i, setRow) => {
            const at = (f: string) => errors[`actors.${i}.${f}`];
            return (
              <div className="grid gap-2 sm:grid-cols-3">
                <TextField
                  label="Actor id"
                  mono
                  value={a.id}
                  issues={at('id')}
                  onChange={(v) => setRow({ ...a, id: v })}
                />
                <SelectField
                  label="Role"
                  value={a.role}
                  options={opts(ACTOR_ROLES)}
                  issues={at('role')}
                  onChange={(v) => setRow({ ...a, role: v ?? '' })}
                />
                <SelectField
                  label="NPC"
                  value={a.npcId ?? ''}
                  none="the actor id"
                  options={ctx.npcs}
                  issues={at('npcId')}
                  onChange={(v) => setRow({ ...a, npcId: v ?? '' })}
                />
                <SelectField
                  label="Anchor"
                  value={a.anchor ?? ''}
                  none="(none)"
                  options={ctx.anchors}
                  issues={at('anchor')}
                  onChange={(v) => setRow({ ...a, anchor: v ?? '' })}
                />
                <NumberField
                  label="Health"
                  integer
                  min={1}
                  value={a.health ?? 100}
                  issues={at('health')}
                  onChange={(v) => setRow({ ...a, health: v ?? 0 })}
                />
                <CheckboxField
                  label="Protected"
                  hint="The squad can't hurt it."
                  checked={!!a.protected}
                  onChange={(v) => setRow({ ...a, protected: v })}
                />
              </div>
            );
          }}
        />
      </Fieldset>

      <Fieldset legend="Objectives">
        <ListEditor
          label="Objectives (at most 8; ↑↓ to order)"
          what="objective"
          items={s.objectives ?? []}
          issues={errors.objectives}
          make={() => ({
            id: `obj${(s.objectives ?? []).length + 1}`,
            type: 'find',
            target: '',
            label: '',
            after: [],
            required: true,
          })}
          onChange={(v) => set({ objectives: v })}
          row={(o, i, setRow) => (
            <ObjectiveRow
              value={o}
              index={i}
              onChange={setRow}
              errors={errors}
              ctx={ctx}
              markOptions={markOptions}
              zoneOptions={zoneOptions}
            />
          )}
        />
      </Fieldset>

      <Fieldset legend="Scenario triggers (in-raid scripting, not scan triggers)">
        <ListEditor
          label="Scenario triggers (at most 24): on / if / do"
          what="scenario trigger"
          items={s.triggers ?? []}
          issues={errors.triggers}
          make={() => ({
            id: `t${(s.triggers ?? []).length + 1}`,
            on: { type: 'start' },
            if: null,
            once: true,
            do: [{ type: 'caption', text: '' }],
          })}
          onChange={(v) => set({ triggers: v })}
          row={(t, i, setRow) => (
            <TriggerRow value={t} index={i} onChange={setRow} errors={errors} ctx={ctx} />
          )}
        />
      </Fieldset>

      <Fieldset legend="Win and lose">
        <p className="text-xs text-bruise sm:col-span-2">
          Won when every required objective is done (or a win action fires). Lost on any lose rule,
          a failed required objective, or a lose action.
        </p>
        <ListEditor
          label="Lose rules (any one loses)"
          what="lose rule"
          items={s.lose ?? []}
          issues={errors.lose}
          make={() => ({ type: 'actor_dead', actor: '' })}
          onChange={(v) => set({ lose: v })}
          row={(l, i, setRow) => (
            <EventEditor
              value={l}
              onChange={setRow}
              errors={errors}
              base={`lose.${i}`}
              ctx={ctx}
              lose
            />
          )}
        />
        <ActionList
          label="On win (server actions)"
          value={s.onWin ?? []}
          onChange={(v) => set({ onWin: v })}
          errors={errors}
          base="onWin"
          ctx={ctx}
          serverOnly
        />
        <ActionList
          label="On lose (server actions)"
          value={s.onLose ?? []}
          onChange={(v) => set({ onLose: v })}
          errors={errors}
          base="onLose"
          ctx={ctx}
          serverOnly
        />
      </Fieldset>

      <DryRunPanel scenario={s} />
    </>
  );
}

function ObjectiveRow({
  value: o,
  index: i,
  onChange,
  errors,
  ctx,
  markOptions,
  zoneOptions,
}: {
  value: ObjectiveDef;
  index: number;
  onChange: (v: ObjectiveDef) => void;
  errors: FieldErrors;
  ctx: Ctx;
  markOptions: Option[];
  zoneOptions: Option[];
}) {
  const at = (f: string) => errors[`objectives.${i}.${f}`];
  const set = (patch: Partial<ObjectiveDef>) => onChange({ ...o, ...patch });
  const afterIssues = Object.values(relative(errors, `objectives.${i}.after`)).flat();
  const actors = ctx.actors.map((a) => ({ value: a, label: a }));
  const num = (
    k: 'holdS' | 'waves' | 'waveSize' | 'waveGrowth',
    label: string,
    hint?: string,
    integer = true,
  ) => (
    <NumberField
      label={label}
      hint={hint}
      integer={integer}
      min={0}
      value={o[k] ?? 0}
      issues={at(k)}
      onChange={(v) => set({ [k]: v ?? 0 })}
    />
  );
  const target = (hint: string) => (
    <TextField
      label="Target"
      mono
      value={o.target}
      hint={hint}
      issues={at('target')}
      onChange={(v) => set({ target: v })}
    />
  );
  const waves = () => (
    <>
      {num('waves', 'Waves', o.type === 'hold' ? '0 = hold for holdS seconds instead.' : undefined)}
      {num('waveSize', 'Wave size')}
      {num('waveGrowth', 'Growth per wave')}
      <SelectField
        label="Spawn anchor"
        value={o.spawn ?? ''}
        none="the entrance"
        options={ctx.anchors}
        issues={at('spawn')}
        onChange={(v) => set({ spawn: v ?? '' })}
      />
    </>
  );
  return (
    <div className="grid gap-2 sm:grid-cols-3">
      <TextField
        label="Objective id"
        mono
        value={o.id}
        issues={at('id')}
        onChange={(v) => set({ id: v })}
      />
      <SelectField
        label="Type"
        value={o.type}
        options={opts(OBJECTIVE_TYPES)}
        issues={at('type')}
        onChange={(v) => set({ type: v ?? '' })}
      />
      <TextField
        label="Label"
        value={o.label}
        hint="The tracker's line."
        issues={at('label')}
        onChange={(v) => set({ label: v })}
      />
      <CheckboxField
        label="Required"
        hint="Off = a bonus objective that never blocks the win."
        checked={o.required ?? true}
        onChange={(v) => set({ required: v })}
      />
      <CheckboxField
        label="Hidden"
        hint="On = a helper kept off the HUD list and the toasts; it still progresses (ObjectiveDef.Hidden)."
        checked={o.hidden ?? false}
        onChange={(v) => set({ hidden: v })}
      />
      <Chips
        label={`After (objective ${i + 1})`}
        options={ctx.objectives.filter((x) => x !== o.id).map((x) => ({ value: x, label: x }))}
        value={o.after ?? []}
        hint="Objectives that must be done first."
        issues={afterIssues}
        onChange={(v) => set({ after: v })}
      />
      {o.type === 'find' && target('actor:<id>, item:<id> or prop:<id>')}
      {o.type === 'free' && (
        <>
          {target('door:<tag>, prop:<id> or actor:<id>')}
          {num('holdS', 'Hold seconds', '0.5..120', false)}
        </>
      )}
      {o.type === 'hold' && (
        <>
          {waves()}
          {(o.waves ?? 0) === 0 &&
            num('holdS', 'Hold seconds', '5..900 when there are no waves', false)}
          <SelectField
            label="Zone"
            value={o.zone ?? ''}
            none="(none)"
            options={zoneOptions}
            issues={at('zone')}
            onChange={(v) => set({ zone: v ?? '' })}
          />
        </>
      )}
      {o.type === 'crescendo' && (
        <>
          {target('The prop to interact with (prop:generator)')}
          {waves()}
        </>
      )}
      {o.type === 'escort' && (
        <>
          <SelectField
            label="Actor"
            value={o.actor ?? ''}
            options={actors}
            issues={at('actor')}
            onChange={(v) => set({ actor: v ?? '' })}
          />
          <SelectField
            label="To"
            value={o.to || 'exit'}
            options={markOptions}
            issues={at('to')}
            onChange={(v) => set({ to: v ?? 'exit' })}
          />
        </>
      )}
      {o.type === 'kill_target' && (
        <SelectField
          label="Actor"
          value={o.actor ?? ''}
          options={actors}
          issues={at('actor') ?? at('target')}
          onChange={(v) => set({ actor: v ?? '' })}
        />
      )}
      {o.type === 'no_kill' && (
        <p className="text-xs text-bruise sm:col-span-2">
          Fails when an innocent is killed; done when the scenario is won.
        </p>
      )}
      {o.type === 'reach_exit' && (
        <p className="text-xs text-bruise sm:col-span-2">Done when the squad extracts.</p>
      )}
    </div>
  );
}

function TriggerRow({
  value: t,
  index: i,
  onChange,
  errors,
  ctx,
}: {
  value: ScenarioTriggerDef;
  index: number;
  onChange: (v: ScenarioTriggerDef) => void;
  errors: FieldErrors;
  ctx: Ctx;
}) {
  const base = `triggers.${i}`;
  const varKeys = [...new Set([...ctx.flags.map((f) => `flag.${f.key ?? ''}`)])];
  return (
    <div className="space-y-2">
      <div className="grid gap-2 sm:grid-cols-3">
        <TextField
          label="Trigger id"
          mono
          value={t.id}
          issues={errors[`${base}.id`]}
          onChange={(v) => onChange({ ...t, id: v })}
        />
        <CheckboxField
          label="Once"
          hint="Fires at most once."
          checked={!!t.once}
          onChange={(v) => onChange({ ...t, once: v })}
        />
      </div>
      <div className="grid gap-2 lg:grid-cols-3">
        <div className="rounded border border-newsprint p-1">
          <p className="text-xs font-bold">ON</p>
          <EventEditor
            value={t.on ?? { type: '' }}
            onChange={(on) => onChange({ ...t, on })}
            errors={errors}
            base={`${base}.on`}
            ctx={ctx}
          />
          <IssueList issues={errors[`${base}.on`]} />
        </div>
        <div className="rounded border border-newsprint p-1">
          <p className="text-xs font-bold">IF</p>
          <ConditionPicker
            label={`Trigger ${i + 1} condition`}
            value={t.if as CondDef | null | undefined}
            onChange={(c) => onChange({ ...t, if: c as ScenarioTriggerDef['if'] })}
            hints={{ allowRaid: false, vars: varKeys, namespaces: SCENARIO_NAMESPACES }}
            issues={relative(errors, `${base}.if`)}
          />
          <p className="text-xs text-bruise">
            Reads var.&lt;key&gt; (signed vars, flag.* snapshots), objective.&lt;id&gt;,
            actor.&lt;id&gt;[.health], found.&lt;target&gt;, timer.&lt;id&gt;,
            raid.innocents_killed.
          </p>
          <IssueList issues={errors[`${base}.if`]} />
        </div>
        <div className="rounded border border-newsprint p-1">
          <p className="text-xs font-bold">DO</p>
          <ActionList
            label="Actions (1..6)"
            value={t.do ?? []}
            onChange={(v) => onChange({ ...t, do: v })}
            errors={errors}
            base={`${base}.do`}
            ctx={ctx}
          />
        </div>
      </div>
    </div>
  );
}

/** A scenario event (a trigger's `on`) or a lose rule: the type, then the fields that type reads. */
function EventEditor({
  value: e,
  onChange,
  errors,
  base,
  ctx,
  lose,
}: {
  value: ScenarioEventDef;
  onChange: (v: ScenarioEventDef) => void;
  errors: FieldErrors;
  base: string;
  ctx: Ctx;
  lose?: boolean;
}) {
  const at = (f: string) => errors[`${base}.${f}`];
  const set = (patch: Partial<ScenarioEventDef>) => onChange({ ...e, ...patch });
  const pick = (
    k: 'objective' | 'actor' | 'id',
    label: string,
    options: Option[],
    none?: string,
  ) => (
    <SelectField
      label={label}
      value={e[k] ?? ''}
      none={none}
      options={options}
      issues={at(k)}
      onChange={(v) => set({ [k]: v ?? '' })}
    />
  );
  const text = (k: 'room' | 'id' | 'state', label: string, hint?: string) => (
    <TextField
      label={label}
      mono
      hint={hint}
      value={e[k]}
      issues={at(k)}
      onChange={(v) => set({ [k]: v })}
    />
  );
  const actors = ctx.actors.map((a) => ({ value: a, label: a }));
  return (
    <div className="grid gap-2">
      <SelectField
        label={lose ? 'Lose when' : 'Event'}
        value={e.type}
        options={opts(lose ? LOSE_TYPES : SCENARIO_EVENT_TYPES)}
        issues={at('type')}
        onChange={(v) => onChange({ type: v ?? '', ...(e.onFail ? { onFail: e.onFail } : {}) })}
      />
      {lose && (
        <SelectField
          label="On fail (story issues)"
          value={e.onFail ?? ''}
          options={STORY_ON_FAIL.map((v) => ({ value: v, label: v || 'lose (default)' }))}
          hint="Read only in a story issue: rewind = back to the dog-ear (Mara dies); branch = carry on with fewer (ADR 0042)."
          issues={at('onFail')}
          onChange={(v) => set({ onFail: v ?? '' })}
        />
      )}
      {e.type === 'enter_room' &&
        text(
          'room',
          'Room',
          'A set piece of this scenario, a room template, or room:N; empty = any.',
        )}
      {e.type === 'objective' && (
        <>
          {pick(
            'objective',
            'Objective',
            ctx.objectives.map((o) => ({ value: o, label: o })),
          )}
          <SelectField
            label="State"
            value={e.state ?? ''}
            none="any"
            options={opts(['started', 'done', 'failed', 'revealed'])}
            issues={at('state')}
            onChange={(v) => set({ state: v ?? '' })}
          />
        </>
      )}
      {e.type === 'timer' &&
        (lose ? (
          <NumberField
            label="Seconds"
            min={5}
            max={900}
            value={e.s ?? 0}
            issues={at('s')}
            onChange={(v) => set({ s: v ?? 0 })}
          />
        ) : (
          pick(
            'id',
            'Timer',
            ctx.timers.map((t) => ({ value: t, label: t })),
          )
        ))}
      {e.type === 'actor_health' && (
        <>
          {pick('actor', 'Actor', actors)}
          <NumberField
            label="Below %"
            integer
            min={1}
            max={100}
            value={e.belowPct ?? 0}
            issues={at('belowPct')}
            onChange={(v) => set({ belowPct: v ?? 0 })}
          />
        </>
      )}
      {(e.type === 'actor_dead' || e.type === 'reached') &&
        pick('actor', 'Actor', actors, lose ? undefined : 'any actor')}
      {e.type === 'reached' && text('id', 'Mark', 'exit or setPiece.anchor; empty = any.')}
      {e.type === 'horde_level' && (
        <NumberField
          label="Level %"
          integer
          min={1}
          max={100}
          value={e.belowPct ?? 0}
          issues={at('belowPct')}
          onChange={(v) => set({ belowPct: v ?? 0 })}
        />
      )}
      {e.type === 'beat_outcome' && (
        <>
          {pick('id', 'Beat chain', ctx.beats)}
          {text('state', 'Outcome', 'Empty = any outcome.')}
        </>
      )}
      {e.type === 'var' && (
        <>
          {text('id', 'Var key', 'e.g. hidden.okafor.bitten (checked at start)')}
          {text('state', 'Value', 'Empty = any value.')}
        </>
      )}
      {['pick_up', 'door', 'interact', 'wave_spawned', 'wave_cleared'].includes(e.type ?? '') &&
        text(
          'id',
          e.type === 'door' ? 'Door tag' : e.type?.startsWith('wave') ? 'Wave n' : 'Target',
          'Empty = any.',
        )}
    </div>
  );
}

/** A fresh action of a type with the fields it reads (the record stays flat: one action, the fields its type uses). */
function newAction(type: string): ScenarioActionDef {
  switch (type) {
    case 'timer':
      return { type, id: '', s: 30, visible: true };
    case 'effect':
      return { type, effect: newEffect('give_items') as ScenarioActionDef['effect'] };
    case 'objective':
      return { type, id: '', set: 'done' };
    case 'actor':
      return { type, actor: '', set: 'following' };
    case 'door':
      return { type, id: '', set: 'unlock' };
    case 'lights':
      return { type, set: 'off' };
    case 'container':
      return { type, id: '', set: 'off' };
    case 'hud':
      return { type, id: 'order_ring', set: 'off' };
    default:
      return { type };
  }
}

function ActionList({
  label,
  value,
  onChange,
  errors,
  base,
  ctx,
  serverOnly,
}: {
  label: string;
  value: ScenarioActionDef[];
  onChange: (v: ScenarioActionDef[]) => void;
  errors: FieldErrors;
  base: string;
  ctx: Ctx;
  serverOnly?: boolean;
}) {
  return (
    <ListEditor
      label={label}
      what="action"
      items={value}
      issues={errors[base]}
      make={() => newAction(serverOnly ? 'set_flag' : 'caption')}
      onChange={onChange}
      row={(a, k, setRow) => (
        <ActionEditor
          value={a}
          onChange={setRow}
          errors={errors}
          base={`${base}.${k}`}
          ctx={ctx}
          serverOnly={serverOnly}
        />
      )}
    />
  );
}

function ActionEditor({
  value: a,
  onChange,
  errors,
  base,
  ctx,
  serverOnly,
}: {
  value: ScenarioActionDef;
  onChange: (v: ScenarioActionDef) => void;
  errors: FieldErrors;
  base: string;
  ctx: Ctx;
  serverOnly?: boolean;
}) {
  const at = (f: string) => errors[`${base}.${f}`];
  const set = (patch: Partial<ScenarioActionDef>) => onChange({ ...a, ...patch });
  const actors = ctx.actors.map((x) => ({ value: x, label: x }));
  const text = (k: 'text' | 'id' | 'key' | 'value', label: string, hint?: string) => (
    <TextField
      label={label}
      hint={hint}
      mono={k !== 'text'}
      value={a[k]}
      issues={at(k)}
      onChange={(v) => set({ [k]: v })}
    />
  );
  const count = (label: string, hint?: string) => (
    <NumberField
      label={label}
      hint={hint}
      integer
      min={0}
      value={Number(a.value) || 0}
      issues={at('value')}
      onChange={(v) => set({ value: String(v ?? 0) })}
    />
  );
  const anchor = (label: string) => (
    <SelectField
      label={label}
      value={a.key ?? ''}
      none="the entrance"
      options={ctx.anchors}
      issues={at('key')}
      onChange={(v) => set({ key: v ?? '' })}
    />
  );
  const flag = a.type === 'set_flag' ? findFlag(ctx.flags, a.key ?? '') : undefined;
  return (
    <div className="grid gap-2">
      <SelectField
        label="Action"
        value={a.type}
        options={opts(serverOnly ? SERVER_ACTIONS : SCENARIO_ACTION_TYPES)}
        issues={at('type')}
        onChange={(v) => onChange(newAction(v ?? ''))}
      />
      {a.type === 'beat' && (
        <SelectField
          label="Beat chain"
          value={a.beatId ?? ''}
          options={ctx.beats}
          issues={at('beatId')}
          onChange={(v) => set({ beatId: v ?? '' })}
        />
      )}
      {a.type === 'spawn' && (
        <>
          <SelectField
            label="Zombie"
            value={a.id ?? ''}
            options={ctx.zombies}
            issues={at('id')}
            onChange={(v) => set({ id: v ?? '' })}
          />
          {count('Count')}
          {anchor('At anchor')}
        </>
      )}
      {a.type === 'wave' && (
        <>
          {count('Wave size')}
          {anchor('At anchor')}
        </>
      )}
      {a.type === 'horde_fill' && count('Add % to HORDE')}
      {a.type === 'objective' && (
        <>
          <SelectField
            label="Objective"
            value={a.id ?? ''}
            options={ctx.objectives.map((o) => ({ value: o, label: o }))}
            issues={at('id')}
            onChange={(v) => set({ id: v ?? '' })}
          />
          <SelectField
            label="Set to"
            value={a.set ?? ''}
            options={opts(['revealed', 'started', 'done', 'failed'])}
            issues={at('set')}
            onChange={(v) => set({ set: v ?? '' })}
          />
        </>
      )}
      {a.type === 'actor' && (
        <>
          <SelectField
            label="Actor"
            value={a.actor ?? ''}
            options={actors}
            issues={at('actor')}
            onChange={(v) => set({ actor: v ?? '' })}
          />
          <SelectField
            label="State"
            value={a.set ?? ''}
            options={opts(ACTOR_STATES)}
            issues={at('set')}
            onChange={(v) => set({ set: v ?? '' })}
          />
        </>
      )}
      {a.type === 'door' && (
        <>
          {text('id', 'Door tag')}
          <SelectField
            label="Do"
            value={a.set ?? ''}
            options={opts(DOOR_OPS)}
            issues={at('set')}
            onChange={(v) => set({ set: v ?? '' })}
          />
          {a.set === 'sealed' &&
            text('text', 'Caption', 'The word over the sealed door (default SEALED). Sealed: no OPEN, FORCE or kick.')}
        </>
      )}
      {a.type === 'container' && (
        <>
          {text('id', 'Container', 'A scripted tag (cache), #index, anchor:<id> or * for all.')}
          <SelectField
            label="Switch"
            value={a.set ?? 'off'}
            options={opts(['off', 'on'])}
            issues={at('set')}
            onChange={(v) => set({ set: v ?? 'off' })}
          />
        </>
      )}
      {a.type === 'hud' && (
        <>
          <SelectField
            label="HUD control"
            value={a.id ?? ''}
            options={opts(HUD_CONTROLS)}
            issues={at('id')}
            onChange={(v) => set({ id: v ?? '' })}
          />
          <SelectField
            label="Switch"
            value={a.set ?? 'off'}
            options={opts(['off', 'on'])}
            issues={at('set')}
            onChange={(v) => set({ set: v ?? 'off' })}
          />
        </>
      )}
      {a.type === 'lights' && (
        <SelectField
          label="Power"
          value={a.set ?? 'off'}
          options={opts(['off', 'on'])}
          issues={at('set')}
          onChange={(v) => set({ set: v ?? 'off' })}
        />
      )}
      {a.type === 'timer' && (
        <>
          {text('id', 'Timer id', 'A trigger listens for it with on: timer.')}
          <NumberField
            label="Seconds"
            min={5}
            max={900}
            value={a.s ?? 0}
            issues={at('s')}
            onChange={(v) => set({ s: v ?? 0 })}
          />
          <CheckboxField
            label="Visible on the tracker"
            checked={a.visible ?? true}
            onChange={(v) => set({ visible: v })}
          />
        </>
      )}
      {a.type === 'caption' && text('text', 'Caption')}
      {a.type === 'bark' && (
        <>
          <SelectField
            label="Actor"
            value={a.actor ?? ''}
            options={actors}
            issues={at('actor')}
            onChange={(v) => set({ actor: v ?? '' })}
          />
          {text('text', 'Line')}
        </>
      )}
      {a.type === 'effect' && (
        <EffectListEditor
          label="Engine effect (applied by the server on submit)"
          value={a.effect ? [a.effect as EffectDef] : []}
          onChange={(list) => set({ effect: (list.at(-1) ?? null) as ScenarioActionDef['effect'] })}
          roles={['player', 'leader']}
          errors={errors}
          base={`${base}.effect`}
        />
      )}
      {a.type === 'set_flag' && (
        <>
          {text(
            'key',
            'Flag key',
            'A key the world flag catalogue allows (npc.okafor matches npc.{id}).',
          )}
          {flag?.type === 'enum' ? (
            <SelectField
              label="Value"
              value={a.value ?? ''}
              options={opts(flag.values ?? [])}
              issues={at('value')}
              onChange={(v) => set({ value: v ?? '' })}
            />
          ) : (
            text('value', 'Value', flag ? `A ${flag.type ?? ''} value.` : undefined)
          )}
        </>
      )}
      {a.type === 'recruit' && (
        <SelectField
          label="Recruit"
          value={a.npc ?? ''}
          options={[...actors, ...ctx.npcs.filter((n) => !ctx.actors.includes(n.value))]}
          issues={at('npc')}
          onChange={(v) => set({ npc: v ?? '' })}
        />
      )}
      {a.type &&
        SERVER_ACTIONS.includes(a.type as (typeof SERVER_ACTIONS)[number]) &&
        !serverOnly && (
          <p className="text-xs text-bruise">
            Server action: applied from content after a clean verdict.
          </p>
        )}
    </div>
  );
}

// ----- dry run -----------------------------------------------------------------------------------------------------------------

const varsText = (s: ScenarioDef) =>
  [
    ...new Set(
      (s.triggers ?? []).flatMap((t) => [
        ...condFacts(t?.if as CondDef | null | undefined)
          .filter((f) => f.startsWith('var.'))
          .map((f) => f.slice(4)),
        ...(t?.on?.type === 'var' && t.on.id ? [t.on.id] : []),
      ]),
    ),
  ]
    .map((k) => `${k}=`)
    .join('\n');

const parseVars = (text: string) =>
  Object.fromEntries(
    text
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l && l.includes('='))
      .map((l) => [l.slice(0, l.indexOf('=')).trim(), l.slice(l.indexOf('=') + 1).trim()]),
  );

const verdictCls = (v: string) =>
  v === 'won' ? 'bg-nonphoto' : v === 'lost' ? 'bg-blam text-white' : 'bg-caption/50';

/** The editor's dry run (`lib/scenarioDryRun.ts`): a scripted event list, Run, the step log and the verdict. */
function DryRunPanel({ scenario }: { scenario: ScenarioDef }) {
  const [script, setScript] = useState<DryRunEvent[]>(() => suggestScript(scenario));
  const [vars, setVars] = useState(() => varsText(scenario));
  const [result, setResult] = useState<DryRunResult | null>(null);
  return (
    <fieldset className="mb-4 rounded border-2 border-ink bg-white p-3">
      <legend className="px-1 text-sm font-bold">Dry run</legend>
      <p className="mb-2 text-xs text-bruise">
        Steps this draft through a browser copy of ScenarioRules. The server's shared-cs rules are
        authoritative (and also check distances from the real layout).
      </p>
      <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <ListEditor
          label="Scripted events"
          what="event"
          items={script}
          onChange={setScript}
          make={() => ({ atS: (script.at(-1)?.atS ?? 0) + 5, ev: 'found', arg: '' })}
          row={(e, i, setRow) => (
            <div className="grid grid-cols-[5rem_minmax(0,1fr)_minmax(0,2fr)] gap-1">
              <NumberInput
                label={`Event ${i + 1} seconds`}
                min={0}
                value={e.atS}
                onChange={(v) => setRow({ ...e, atS: v ?? 0 })}
              />
              <SelectInput
                label={`Event ${i + 1} type`}
                value={e.ev}
                options={opts(DRY_RUN_EVENTS)}
                onChange={(v) => setRow({ ...e, ev: v ?? '' })}
              />
              <input
                aria-label={`Event ${i + 1} arg`}
                className={`${inputCls} font-mono`}
                value={e.arg ?? ''}
                placeholder={DRY_RUN_ARG_HINT[e.ev] ?? ''}
                onChange={(x) => setRow({ ...e, arg: x.target.value })}
              />
            </div>
          )}
        />
        <Field
          label="Vars (key=value per line)"
          hint="The ticket's signed vars, e.g. hidden.okafor.bitten=true."
        >
          {(id) => (
            <textarea
              id={id}
              className={`${inputCls} h-24 font-mono text-xs`}
              value={vars}
              onChange={(e) => setVars(e.target.value)}
            />
          )}
        </Field>
      </div>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          className={smallBtn}
          onClick={() => setResult(dryRun(scenario, script, { vars: parseVars(vars) }))}
        >
          Run
        </button>
        <button
          type="button"
          className={smallBtn}
          onClick={() => setScript(suggestScript(scenario))}
        >
          Script from the objectives
        </button>
      </div>
      {result && (
        <div className="mt-3" role="status">
          <p className="text-sm font-bold">
            Verdict:{' '}
            <span className={`rounded px-1 ${verdictCls(result.verdict)}`}>
              {result.verdict
                ? result.verdict.toUpperCase()
                : result.finished
                  ? 'NONE'
                  : 'UNDECIDED (no end event)'}
            </span>
          </p>
          <p className="text-xs">
            Objectives:{' '}
            {Object.entries(result.objectives)
              .map(([k, v]) => `${k} ${v}`)
              .join(' · ')}
          </p>
          {(result.implausible.length > 0 || result.malformed.length > 0) && (
            <p className="text-xs text-blam">
              {[
                ...result.malformed.map((m) => `malformed ${m}`),
                ...result.implausible.map((m) => `implausible ${m}`),
              ].join(' · ')}
            </p>
          )}
          {result.serverActions.length > 0 && (
            <p className="text-xs">Server applies: {result.serverActions.join(' · ')}</p>
          )}
          <table className="mt-1 w-full text-left text-xs">
            <thead>
              <tr>
                <th>s</th>
                <th>Event</th>
                <th>Triggers fired</th>
                <th>Objectives</th>
                <th>Actions</th>
                <th>Verdict</th>
              </tr>
            </thead>
            <tbody>
              {result.steps.map((st, n) => (
                <tr key={n} className="border-t border-newsprint align-top">
                  <td className="font-mono">{st.atS.toFixed(1)}</td>
                  <td className="font-mono">
                    {st.label}
                    {st.notes.length > 0 && (
                      <div className="text-bruise">{st.notes.join('; ')}</div>
                    )}
                  </td>
                  <td>{st.fired.join(', ')}</td>
                  <td>{st.objectives.join(', ')}</td>
                  <td>{st.actions.join('; ')}</td>
                  <td>{st.verdict}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </fieldset>
  );
}

// ----- set piece ---------------------------------------------------------------------------------------------------------------

type Tool =
  | { kind: 'anchor'; anchorKind: string }
  | { kind: 'prop'; model: string }
  | { kind: 'zombie'; zombieId: string };
type Sel = { list: 'anchors' | 'props' | 'zombies'; index: number } | null;

const ANCHOR_COLORS: Record<string, string> = {
  npc_mark: 'var(--color-benday)',
  wave_spawn: 'var(--color-blam)',
  container: 'var(--color-caption)',
  choke: 'var(--color-bruise)',
  fortify: 'var(--color-formaldehyde)',
  mark: 'var(--color-nonphoto)',
  exit: 'var(--color-ink)',
};

/**
 * A set piece (scenario.html §3 "Set-piece placement"): basics, the footprint, a grid editor (1 cell = 1 m; click a cell to place
 * the selected tool at its centre, click an item to select it), exact x/y/yaw lists, and the API's placement preview over 12 seeds.
 */
export function SetPieceForm({ value, onChange, errors }: FormProps<SetPieceDef>) {
  const sp = value;
  const set = (patch: Partial<SetPieceDef>) => onChange({ ...sp, ...patch });
  const b = useContentBundle();
  const zombies = (b.zombies ?? []).map((z) => ({
    value: z.id ?? '',
    label: z.name || (z.id ?? ''),
  }));
  const w = sp.footprint?.w ?? 0;
  const d = sp.footprint?.d ?? 0;
  const anchors = sp.anchors ?? [];
  const props = sp.props ?? [];
  const zs = sp.zombies ?? [];
  const [tool, setTool] = useState<Tool>({ kind: 'anchor', anchorKind: 'npc_mark' });
  const [sel, setSel] = useState<Sel>(null);

  const place = (cx: number, cy: number) => {
    const x = cx + 0.5;
    const y = cy + 0.5;
    if (tool.kind === 'anchor') {
      const taken = new Set(anchors.map((a) => a?.id));
      let n = 1;
      while (taken.has(`${tool.anchorKind}_${n}`)) n++;
      set({
        anchors: [
          ...anchors,
          { id: `${tool.anchorKind}_${n}`, kind: tool.anchorKind, x, y, yawDeg: 0 },
        ],
      });
      setSel({ list: 'anchors', index: anchors.length });
    } else if (tool.kind === 'prop') {
      set({ props: [...props, { model: tool.model, x, y, yawDeg: 0 }] });
      setSel({ list: 'props', index: props.length });
    } else {
      set({ zombies: [...zs, { zombieId: tool.zombieId, x, y }] });
      setSel({ list: 'zombies', index: zs.length });
    }
  };
  const removeSelected = () => {
    if (!sel) return;
    const drop = <T,>(xs: T[]) => xs.filter((_, i) => i !== sel.index);
    if (sel.list === 'anchors') set({ anchors: drop(anchors) });
    if (sel.list === 'props') set({ props: drop(props) });
    if (sel.list === 'zombies') set({ zombies: drop(zs) });
    setSel(null);
  };
  const isSel = (list: NonNullable<Sel>['list'], i: number) =>
    sel?.list === list && sel.index === i;
  const gw = Math.max(1, Math.min(16, w));
  const gd = Math.max(1, Math.min(16, d));

  return (
    <>
      <Fieldset legend="Set piece">
        <TextField
          label="Id"
          mono
          value={sp.id}
          issues={errors.id}
          onChange={(v) => set({ id: v })}
        />
        <TextField
          label="Name"
          value={sp.name}
          issues={errors.name}
          onChange={(v) => set({ name: v })}
        />
        <SelectField
          label="Floor template"
          value={sp.templateId ?? ''}
          none="(the room's own)"
          options={(b.roomTemplates ?? []).map((t) => ({ value: t.id ?? '', label: t.id ?? '' }))}
          hint="The room template whose floor style it uses."
          issues={errors.templateId}
          onChange={(v) => set({ templateId: v ?? '' })}
        />
        <NumberField
          label="Placement version"
          integer
          min={1}
          value={sp.placementVersion ?? 1}
          hint="Bump it for any behaviour change; never edit placement in place."
          issues={errors.placementVersion}
          onChange={(v) => set({ placementVersion: v ?? 1 })}
        />
        <Chips
          label="Categories"
          options={(b.categories ?? []).map((c) => ({ value: c.id ?? '', label: c.id ?? '' }))}
          value={sp.categories ?? []}
          hint="Building categories it fits; none = any."
          issues={Object.values(relative(errors, 'categories')).flat()}
          onChange={(v) => set({ categories: v })}
        />
        <Chips
          label="Rotations"
          options={SET_PIECE_ROTATIONS.map((r) => ({ value: String(r), label: `${r}°` }))}
          value={(sp.rotations ?? []).map(String)}
          hint="None = 0° and 90°."
          issues={Object.values(relative(errors, 'rotations')).flat()}
          onChange={(v) => set({ rotations: v.map(Number).sort((x, y) => x - y) })}
        />
        <NumberField
          label="Width (m)"
          integer
          min={SET_PIECE_SIDE.min}
          max={SET_PIECE_SIDE.max}
          value={w}
          issues={errors.footprint}
          onChange={(v) => set({ footprint: { w: v ?? 0, d } })}
        />
        <NumberField
          label="Depth (m)"
          integer
          min={SET_PIECE_SIDE.min}
          max={SET_PIECE_SIDE.max}
          value={d}
          hint="y = 0 at the door side, the back wall at the depth."
          onChange={(v) => set({ footprint: { w, d: v ?? 0 } })}
        />
      </Fieldset>

      <Fieldset legend="Footprint grid">
        <div className="space-y-2 sm:col-span-2">
          <div className="flex flex-wrap items-end gap-2 text-xs">
            <SelectInput
              label="Tool"
              value={tool.kind}
              options={opts(['anchor', 'prop', 'zombie'])}
              onChange={(k) =>
                setTool(
                  k === 'prop'
                    ? { kind: 'prop', model: 'crate' }
                    : k === 'zombie'
                      ? { kind: 'zombie', zombieId: zombies[0]?.value ?? '' }
                      : { kind: 'anchor', anchorKind: 'npc_mark' },
                )
              }
            />
            {tool.kind === 'anchor' && (
              <SelectInput
                label="Anchor kind"
                value={tool.anchorKind}
                options={opts(ANCHOR_KINDS)}
                onChange={(v) => setTool({ kind: 'anchor', anchorKind: v ?? 'npc_mark' })}
              />
            )}
            {tool.kind === 'prop' && (
              <input
                aria-label="Prop model"
                className={`${inputCls} w-40 font-mono`}
                value={tool.model}
                onChange={(e) => setTool({ kind: 'prop', model: e.target.value })}
              />
            )}
            {tool.kind === 'zombie' && (
              <SelectInput
                label="Zombie"
                value={tool.zombieId}
                options={zombies}
                onChange={(v) => setTool({ kind: 'zombie', zombieId: v ?? '' })}
              />
            )}
            <button
              type="button"
              className={`${smallBtn} text-blam`}
              disabled={!sel}
              onClick={removeSelected}
            >
              Remove selected
            </button>
            <span className="text-bruise">Click a cell to place; click an item to select it.</span>
          </div>
          <p className="text-center text-xs text-bruise">door side (y = 0)</p>
          <svg
            role="img"
            aria-label={`Footprint ${w} by ${d} m`}
            viewBox={`-0.2 -0.2 ${gw + 0.4} ${gd + 0.4}`}
            className="mx-auto block w-full max-w-md rounded border-2 border-ink bg-newsprint/40"
          >
            {Array.from({ length: gd }, (_, cy) =>
              Array.from({ length: gw }, (_, cx) => (
                <rect
                  key={`${cx},${cy}`}
                  role="button"
                  aria-label={`Cell ${cx},${cy}`}
                  x={cx}
                  y={cy}
                  width={1}
                  height={1}
                  fill="white"
                  stroke="var(--color-newsprint)"
                  strokeWidth={0.04}
                  className="cursor-pointer hover:fill-caption/40"
                  onClick={() => place(cx, cy)}
                />
              )),
            )}
            {props.map((p, i) =>
              p == null ? null : (
                <g
                  key={`p${i}`}
                  role="button"
                  aria-label={`Prop ${p.model ?? ''}`}
                  onClick={() => setSel({ list: 'props', index: i })}
                  className="cursor-pointer"
                >
                  <rect
                    x={(p.x ?? 0) - 0.35}
                    y={(p.y ?? 0) - 0.35}
                    width={0.7}
                    height={0.7}
                    fill="var(--color-newsprint)"
                    stroke={isSel('props', i) ? 'var(--color-blam)' : 'var(--color-ink)'}
                    strokeWidth={isSel('props', i) ? 0.1 : 0.04}
                    transform={`rotate(${p.yawDeg ?? 0} ${p.x ?? 0} ${p.y ?? 0})`}
                  />
                  <text x={p.x ?? 0} y={(p.y ?? 0) + 0.1} fontSize={0.25} textAnchor="middle">
                    {(p.model ?? '').slice(0, 8)}
                  </text>
                </g>
              ),
            )}
            {zs.map((z, i) =>
              z == null ? null : (
                <g
                  key={`z${i}`}
                  role="button"
                  aria-label={`Zombie ${z.zombieId ?? ''}`}
                  onClick={() => setSel({ list: 'zombies', index: i })}
                  className="cursor-pointer"
                >
                  <circle
                    cx={z.x ?? 0}
                    cy={z.y ?? 0}
                    r={0.3}
                    fill="var(--color-formaldehyde)"
                    stroke={isSel('zombies', i) ? 'var(--color-blam)' : 'var(--color-ink)'}
                    strokeWidth={isSel('zombies', i) ? 0.1 : 0.04}
                  />
                  <text x={z.x ?? 0} y={(z.y ?? 0) + 0.1} fontSize={0.3} textAnchor="middle">
                    Z
                  </text>
                </g>
              ),
            )}
            {anchors.map((a, i) =>
              a == null ? null : (
                <g
                  key={`a${i}`}
                  role="button"
                  aria-label={`Anchor ${a.id ?? ''}`}
                  onClick={() => setSel({ list: 'anchors', index: i })}
                  className="cursor-pointer"
                >
                  <circle
                    cx={a.x ?? 0}
                    cy={a.y ?? 0}
                    r={0.22}
                    fill={ANCHOR_COLORS[a.kind ?? ''] ?? 'var(--color-bruise)'}
                    stroke={isSel('anchors', i) ? 'var(--color-blam)' : 'white'}
                    strokeWidth={isSel('anchors', i) ? 0.1 : 0.05}
                  />
                  <line
                    x1={a.x ?? 0}
                    y1={a.y ?? 0}
                    x2={(a.x ?? 0) + 0.4 * Math.sin(((a.yawDeg ?? 0) * Math.PI) / 180)}
                    y2={(a.y ?? 0) - 0.4 * Math.cos(((a.yawDeg ?? 0) * Math.PI) / 180)}
                    stroke="var(--color-ink)"
                    strokeWidth={0.05}
                  />
                  <text x={a.x ?? 0} y={(a.y ?? 0) - 0.3} fontSize={0.25} textAnchor="middle">
                    {a.id}
                  </text>
                </g>
              ),
            )}
          </svg>
          <p className="text-center text-xs text-bruise">back wall (y = {d})</p>
          <IssueList issues={errors.footprint} />
        </div>
      </Fieldset>

      <Fieldset legend="Anchors, props and zombies">
        <ListEditor
          label="Anchors"
          what="anchor"
          items={anchors}
          issues={errors.anchors}
          make={() => ({ id: '', kind: 'mark', x: 0.5, y: 0.5, yawDeg: 0 })}
          onChange={(v) => set({ anchors: v })}
          row={(a, i, setRow) => {
            const at = (f: string) => errors[`anchors.${i}.${f}`];
            return (
              <div className="grid gap-1 sm:grid-cols-6">
                <TextField
                  label="Anchor id"
                  mono
                  value={a.id}
                  issues={at('id')}
                  onChange={(v) => setRow({ ...a, id: v })}
                />
                <SelectField
                  label="Kind"
                  value={a.kind}
                  options={opts(ANCHOR_KINDS)}
                  issues={at('kind')}
                  onChange={(v) => setRow({ ...a, kind: v ?? '' })}
                />
                <NumberField
                  label="X"
                  value={a.x ?? 0}
                  issues={errors[`anchors.${i}`]}
                  onChange={(v) => setRow({ ...a, x: v ?? 0 })}
                />
                <NumberField
                  label="Y"
                  value={a.y ?? 0}
                  onChange={(v) => setRow({ ...a, y: v ?? 0 })}
                />
                <NumberField
                  label="Yaw°"
                  value={a.yawDeg ?? 0}
                  onChange={(v) => setRow({ ...a, yawDeg: v ?? 0 })}
                />
                {a.kind === 'wave_spawn' && (
                  <SelectField
                    label="Wall"
                    value={a.wall ?? ''}
                    none="(none)"
                    options={opts(['back', 'left', 'right', 'front'])}
                    onChange={(v) => setRow({ ...a, wall: v ?? '' })}
                  />
                )}
                {a.kind === 'container' && (
                  <TextField
                    label="Container"
                    mono
                    value={a.container}
                    hint="e.g. cabinet"
                    onChange={(v) => setRow({ ...a, container: v })}
                  />
                )}
              </div>
            );
          }}
        />
        <ListEditor
          label="Props"
          what="prop"
          items={props}
          issues={errors.props}
          make={() => ({ model: '', x: 0.5, y: 0.5, yawDeg: 0 })}
          onChange={(v) => set({ props: v })}
          row={(p, i, setRow) => (
            <div className="grid gap-1 sm:grid-cols-4">
              <TextField
                label="Model"
                mono
                value={p.model}
                issues={errors[`props.${i}.model`]}
                onChange={(v) => setRow({ ...p, model: v })}
              />
              <NumberField
                label="X"
                value={p.x ?? 0}
                issues={errors[`props.${i}`]}
                onChange={(v) => setRow({ ...p, x: v ?? 0 })}
              />
              <NumberField
                label="Y"
                value={p.y ?? 0}
                onChange={(v) => setRow({ ...p, y: v ?? 0 })}
              />
              <NumberField
                label="Yaw°"
                value={p.yawDeg ?? 0}
                onChange={(v) => setRow({ ...p, yawDeg: v ?? 0 })}
              />
            </div>
          )}
        />
        <ListEditor
          label="Zombies"
          what="zombie"
          items={zs}
          issues={errors.zombies}
          make={() => ({ zombieId: zombies[0]?.value ?? '', x: 0.5, y: 0.5 })}
          onChange={(v) => set({ zombies: v })}
          row={(z, i, setRow) => (
            <div className="grid gap-1 sm:grid-cols-3">
              <SelectField
                label="Zombie"
                value={z.zombieId}
                options={zombies}
                issues={errors[`zombies.${i}.zombieId`]}
                onChange={(v) => setRow({ ...z, zombieId: v ?? '' })}
              />
              <NumberField
                label="X"
                value={z.x ?? 0}
                issues={errors[`zombies.${i}`]}
                onChange={(v) => setRow({ ...z, x: v ?? 0 })}
              />
              <NumberField
                label="Y"
                value={z.y ?? 0}
                onChange={(v) => setRow({ ...z, y: v ?? 0 })}
              />
            </div>
          )}
        />
      </Fieldset>

      <PlacementPreview setPiece={sp} />
    </>
  );
}

const SEEDS = Array.from({ length: 12 }, (_, i) => i + 1);
/** An SVG as an image URL: the markup is never injected into the page. */
const svgUrl = (svg: string) => `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

/** The API's placement preview (`POST /admin/set-pieces/preview`, S1-SCN): 12 seeds rendered as SVG, plus the sweep. */
function PlacementPreview({ setPiece }: { setPiece: SetPieceDef }) {
  const api = useContext(ApiContext);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SetPiecePreviewResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [placement, setPlacement] = useState('deepest');
  const [doorPolicy, setDoorPolicy] = useState('');
  const [minRooms, setMinRooms] = useState(0);
  const run = async () => {
    if (!api) return;
    setBusy(true);
    setError(null);
    try {
      setResult(
        await previewSetPiece(api.client, {
          setPiece,
          seeds: SEEDS,
          category: setPiece.categories?.[0],
          placement,
          doorPolicy,
          minRooms,
        }),
      );
    } catch (e) {
      const text = errorText(e);
      setResult(null);
      setError(
        /404|not found/i.test(text) ? `Preview needs the scenario API (S1-SCN). (${text})` : text,
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <fieldset className="mb-4 rounded border-2 border-ink bg-white p-3">
      <legend className="px-1 text-sm font-bold">Placement preview</legend>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={smallBtn}
          disabled={!api || busy}
          onClick={() => void run()}
        >
          {busy ? 'Rendering…' : 'Preview 12 seeds'}
        </button>
        <label className="flex items-center gap-1 text-xs">
          placement
          <select
            aria-label="Preview placement"
            className={`${inputCls} w-auto`}
            value={placement}
            onChange={(e) => setPlacement(e.target.value)}
          >
            {SET_PIECE_PLACEMENTS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1 text-xs">
          doors
          <select
            aria-label="Preview door policy"
            className={`${inputCls} w-auto`}
            value={doorPolicy}
            onChange={(e) => setDoorPolicy(e.target.value)}
          >
            {DOOR_POLICIES.map((p) => (
              <option key={p} value={p}>
                {p || '(keep)'}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1 text-xs">
          min rooms
          <input
            aria-label="Preview min rooms"
            type="number"
            min={0}
            max={16}
            className={`${inputCls} w-16`}
            value={minRooms}
            onChange={(e) => setMinRooms(Math.max(0, Math.trunc(Number(e.target.value) || 0)))}
          />
        </label>
        {!api && <span className="text-xs text-bruise">Sign in to preview.</span>}
      </div>
      {error && (
        <p role="alert" className="mt-1 text-xs text-blam">
          {error}
        </p>
      )}
      {result && (
        <>
          {result.errors.length > 0 && (
            <ul className="mt-2 text-xs text-blam" aria-label="Set piece errors">
              {result.errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-xs">
            Sweep: {result.sweep.seeds} seeds · {result.sweep.failures} failed placements ·{' '}
            {result.sweep.unreachable} unreachable rooms
            {result.sweep.firstError && (
              <span className="text-blam"> · first: {result.sweep.firstError}</span>
            )}
          </p>
          <ul className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {result.previews.map((p) => (
              <li key={p.seed} className="rounded border border-newsprint p-1 text-xs">
                <img
                  src={svgUrl(p.svg)}
                  alt={`Seed ${p.seed}: ${p.roomCount} rooms`}
                  className="w-full bg-white"
                />
                <span className={p.placed ? '' : 'text-blam'}>
                  seed {p.seed} · {p.roomCount} rooms · {p.placed ? 'placed' : 'NOT placed'}
                  {p.merges ? ` · ${p.merges} merges` : ''}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </fieldset>
  );
}

// ----- world flag --------------------------------------------------------------------------------------------------------------

/** A world flag in the catalogue (scenario.html §3 "World flags"). */
export function WorldFlagForm({ value, onChange, errors }: FormProps<WorldFlagDef>) {
  const f = value;
  const set = (patch: Partial<WorldFlagDef>) => onChange({ ...f, ...patch });
  const values = useMemo(() => f.values ?? [], [f.values]);
  return (
    <Fieldset legend="World flag">
      <TextField
        label="Key"
        mono
        value={f.key}
        hint="Dot-separated a-z, 0-9 and _; {name} matches one segment: bld.{id}.crew, npc.{id}, arc.{id}.issue."
        issues={errors.key}
        onChange={(v) => set({ key: v })}
      />
      <SelectField
        label="Type"
        value={f.type || 'enum'}
        options={opts(WORLD_FLAG_TYPES)}
        issues={errors.type}
        onChange={(v) => set({ type: v ?? 'enum' })}
      />
      {(f.type ?? 'enum') === 'enum' && (
        <StringListEditor
          label="Values"
          value={values}
          errors={errors}
          base="values"
          placeholder="friendly"
          hint="At most 32 characters each."
          onChange={(v) => set({ values: v })}
        />
      )}
      <SelectField
        label="Scope"
        value={f.scope || 'run'}
        options={opts(WORLD_FLAG_SCOPES)}
        hint="run: reset by a run death. Story-arc flags (arc.*) are account."
        issues={errors.scope}
        onChange={(v) => set({ scope: v ?? 'run' })}
      />
      <SelectField
        label="Map badge"
        value={f.badge ?? ''}
        none="none"
        options={opts(MAP_BADGE_KINDS)}
        hint="A pin shows at most one badge: the highest priority."
        issues={errors.badge}
        onChange={(v) => set({ badge: v ?? '' })}
      />
      <NumberField
        label="Priority"
        integer
        value={f.priority ?? 0}
        issues={errors.priority}
        onChange={(v) => set({ priority: v ?? 0 })}
      />
      <TextField
        label="Description"
        multiline
        value={f.description}
        onChange={(v) => set({ description: v })}
      />
    </Fieldset>
  );
}
